import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Empty, Input, InputNumber, Segmented, Space, Spin, Tag, Tooltip } from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  CompressOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useProjectStore } from '../../hooks/useProjectStore';
import { useLogging, LogCurve, curveColor, LTH } from '../logging/logShared';
import DepthCursorOverlay from './DepthCursorOverlay';
import DepthValuePanel from './DepthValuePanel';
import type { DepthValueItem } from './DepthValuePanel';
import type { ImportedCurveData } from './types';
import { findNearestByDepth, isFiniteDepthValue } from './sweetSpotDepth';
import { useDepthInteraction, useDepthWheel } from './useDepthInteraction';
import { sampleRows } from './sweetSpotCurveCatalog';
import { useSweetSpotWorkspaceStore } from './sweetSpotWorkspaceStore';
import './SweetSpotWorkbench.css';

type TrackGroup = {
  key: string;
  title: string;
  aliases: string[];
  logScale?: boolean;
  order: number;
};

type CurveDisplayMeta = {
  title: string;
  order: number;
  logScale?: boolean;
};

type SingleCurveTrack = {
  curve: LogCurve;
  group: TrackGroup;
  base: string;
};

type CurveScaleMode = 'auto' | 'linear' | 'log';
type CurveRangeMode = 'auto' | 'manual';

type CurveTrackSettings = {
  visible: boolean;
  label: string;
  color: string;
  lineWidth: number;
  trackWidth: number;
  scale: CurveScaleMode;
  rangeMode: CurveRangeMode;
  min: number | null;
  max: number | null;
};

type CurveViewLayout = {
  order: string[];
  tracks: Record<string, CurveTrackSettings>;
  showDepthValues: boolean;
  libraryOpen: boolean;
};

type ConfiguredCurveTrack = SingleCurveTrack & {
  key: string;
  settings: CurveTrackSettings;
};

const GROUPS: TrackGroup[] = [
  { key: 'lithology', title: '岩性/井眼', aliases: ['GR', 'SP', 'CAL', 'CALI', 'BS'], order: 10 },
  { key: 'resistivity', title: '电阻率系列', aliases: ['RT', 'RD', 'ILD', 'RILD', 'LLD', 'RI', 'IM', 'ILM', 'RS', 'ILS', 'LLS', 'MSFL', 'RXO', 'RW'], logScale: true, order: 20 },
  { key: 'porosity', title: '孔隙度/密度/声波', aliases: ['DEN', 'RHOB', 'CNL', 'CN', 'NPHI', 'AC', 'DT', 'DTC', 'DTCO', 'DTS', 'POR', 'PORO', 'PHI', 'PE'], order: 30 },
  { key: 'special', title: '特殊测井/轨迹', aliases: ['PE', 'PEF', 'DEV', 'INC', 'AZI', 'AZIM', 'NMR', 'T2', 'FMI', 'UBI', 'VP', 'VS', 'DTS', 'DTN'], order: 40 },
  { key: 'engineering', title: '解释/工程参数', aliases: ['TOC', 'BI', 'BRIT', 'PERM', 'K', 'YM', 'YME', 'PR', 'POIS', 'SHMIN', 'BIOT', 'SW', 'VSH'], order: 50 },
];

const OTHER_GROUP: TrackGroup = { key: 'other', title: '其他曲线', aliases: [], order: 90 };
const CURVE_DISPLAY: Record<string, CurveDisplayMeta> = {
  GR: { title: '自然伽马', order: 10 },
  SP: { title: '自然电位', order: 11 },
  CAL: { title: '井径', order: 12 },
  BS: { title: '钻头尺寸', order: 13 },
  RT: { title: '地层真电阻率', order: 20, logScale: true },
  RD: { title: '深探测电阻率', order: 21, logScale: true },
  ILD: { title: '深感应电阻率', order: 22, logScale: true },
  RI: { title: '中探测电阻率', order: 23, logScale: true },
  IM: { title: '中感应电阻率', order: 24, logScale: true },
  RS: { title: '浅探测电阻率', order: 25, logScale: true },
  ILS: { title: '浅感应电阻率', order: 26, logScale: true },
  MSFL: { title: '微球形聚焦电阻率', order: 27, logScale: true },
  RXO: { title: '冲洗带电阻率', order: 28, logScale: true },
  RW: { title: '地层水电阻率', order: 29, logScale: true },
  DEN: { title: '岩性密度', order: 30 },
  CNL: { title: '补偿中子孔隙度', order: 31 },
  AC: { title: '声波时差', order: 32 },
  DTS: { title: '横波时差', order: 33 },
  PE: { title: '光电吸收截面指数', order: 34 },
  POR: { title: '解释孔隙度', order: 35 },
  SW: { title: '含水饱和度', order: 36 },
  VSH: { title: '泥质含量', order: 37 },
  PERM: { title: '解释渗透率', order: 50 },
  TOC: { title: '总有机碳', order: 51 },
  BI: { title: '脆性指数', order: 52 },
  YM: { title: '杨氏模量', order: 53 },
  PR: { title: '泊松比', order: 54 },
  SHMIN: { title: '最小水平主应力', order: 55 },
  BIOT: { title: 'Biot系数', order: 56 },
  DEV: { title: '井斜角', order: 60 },
  AZI: { title: '方位角', order: 61 },
  NMR: { title: '核磁 T2', order: 62 },
  T2: { title: '核磁 T2', order: 63 },
  FMI: { title: '成像测井', order: 64 },
  UBI: { title: '超声成像测井', order: 65 },
  VP: { title: '纵波速度', order: 66 },
  VS: { title: '横波速度', order: 67 },
  DTN: { title: '正常压实声波', order: 68 },
};
const MNEMONIC_ALIAS: Record<string, string> = {
  CALI: 'CAL',
  CALIPER: 'CAL',
  BIT: 'BS',
  BITSIZE: 'BS',
  RHOB: 'DEN',
  CN: 'CNL',
  NPHI: 'CNL',
  DTC: 'AC',
  DTCO: 'AC',
  DT: 'AC',
  LLD: 'ILD',
  RILD: 'ILD',
  ILM: 'IM',
  LLS: 'RS',
  PEF: 'PE',
  INC: 'DEV',
  AZIM: 'AZI',
  BRIT: 'BI',
  YME: 'YM',
  POIS: 'PR',
  K: 'PERM',
  RSHAL: 'RS',
};

const LEFT_AXIS_WIDTH = 58;
const DEFAULT_TRACK_WIDTH = 136;
const COMPACT_TRACK_WIDTH = 104;
const TRACK_GAP = 6;
const PLOT_TOP = 82;
const PLOT_BOTTOM = 28;
const CURVE_VIEW_LAYOUT_PREFIX = 'fracsight:curve-view-layout:v2:';
const LEGACY_CURVE_VIEW_STORAGE_PREFIX = 'fracsight:curve-view-selection:';
const CURVE_COLORS = ['#2F6FED', '#C2533A', '#16856B', '#8E5BB7', '#D98716', '#008AA6', '#6B7280', '#D14D72'];

function baseMnemonic(mnemonic: string) {
  const cleaned = String(mnemonic || '')
    .toUpperCase()
    .replace(/_\d+$/, '')
    .replace(/[^0-9A-Z]/g, '');
  return MNEMONIC_ALIAS[cleaned] || cleaned;
}

function groupFor(base: string) {
  return GROUPS.find((group) => group.aliases.includes(base)) || OTHER_GROUP;
}

function displayMetaFor(base: string, group: TrackGroup): CurveDisplayMeta {
  return CURVE_DISPLAY[base] || { title: group.title, order: group.order };
}

function finiteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function formatValue(value: number) {
  if (!Number.isFinite(value)) return '-';
  const abs = Math.abs(value);
  if ((abs >= 1000 || abs < 0.01) && abs !== 0) return value.toExponential(1);
  if (abs >= 100) return value.toFixed(0);
  if (abs >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

function drawTextFit(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  align: CanvasTextAlign = 'left',
) {
  if (maxWidth <= 0) return;
  ctx.textAlign = align;
  if (ctx.measureText(text).width <= maxWidth) {
    ctx.fillText(text, x, y);
    return;
  }
  let out = text;
  while (out.length > 1 && ctx.measureText(`${out}...`).width > maxWidth) {
    out = out.slice(0, -1);
  }
  ctx.fillText(`${out}...`, x, y);
}

function buildCurveTracks(curves: LogCurve[]): SingleCurveTrack[] {
  return curves
    .filter((curve) => Array.isArray(curve.data_points) && curve.data_points.length > 0)
    .map((curve) => {
      const base = baseMnemonic(curve.mnemonic);
      return { curve, base, group: groupFor(base) };
    })
    .sort((a, b) => (
      displayMetaFor(a.base, a.group).order - displayMetaFor(b.base, b.group).order
      || a.base.localeCompare(b.base)
      || String(a.curve.mnemonic).localeCompare(String(b.curve.mnemonic))
    ));
}

function curveTrackKey(curve: LogCurve) {
  return `${curve.id}:${curve.mnemonic}`;
}

export function buildImportedViewCurves(importedData: ImportedCurveData): LogCurve[] {
  const sampled = sampleRows(importedData.mappedData, 2000);
  return importedData.columns
    .filter((column) => column.mappedField !== 'depth')
    .map((column, index) => {
      const valueKey = column.mappedField || column.sourceKey;
      const data_points = sampled.flatMap((point) => {
        const value = point[valueKey];
        return typeof value === 'number' && Number.isFinite(value) ? [{ depth: point.depth, value }] : [];
      });
      const values = data_points.map((point) => point.value);
      return {
        id: -(index + 1), mnemonic: column.displayName,
        unit: column.mappedField ? column.targetUnit || column.unit || '' : column.unit || '',
        description: column.recognizedName || column.description || '导入曲线',
        min_value: values.length ? Math.min(...values) : null,
        max_value: values.length ? Math.max(...values) : null,
        data_points,
      };
    });
}

function defaultTrackSettings(track: SingleCurveTrack, visible = true): CurveTrackSettings {
  const description = String(track.curve.description || '').trim();
  const label = CURVE_DISPLAY[track.base]?.title
    || (description && description !== '导入曲线' ? description : track.curve.mnemonic);
  return {
    visible,
    label,
    color: curveColor(track.base),
    lineWidth: 1.4,
    trackWidth: DEFAULT_TRACK_WIDTH,
    scale: 'auto',
    rangeMode: 'auto',
    min: null,
    max: null,
  };
}

function readStoredJson(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function storedStringArray(value: unknown): string[] | null {
  return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : null;
}

function finiteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function sanitizeTrackSettings(value: unknown, fallback: CurveTrackSettings): CurveTrackSettings {
  if (!value || typeof value !== 'object') return fallback;
  const input = value as Partial<CurveTrackSettings>;
  const color = typeof input.color === 'string' && /^#[0-9A-F]{6}$/i.test(input.color)
    ? input.color
    : fallback.color;
  return {
    visible: typeof input.visible === 'boolean' ? input.visible : fallback.visible,
    label: typeof input.label === 'string' && input.label.trim() ? input.label.trim().slice(0, 40) : fallback.label,
    color,
    lineWidth: finiteNumber(input.lineWidth) ? Math.min(4, Math.max(0.6, input.lineWidth)) : fallback.lineWidth,
    trackWidth: finiteNumber(input.trackWidth)
      ? Math.min(360, Math.max(96, Math.round(input.trackWidth)))
      : fallback.trackWidth,
    scale: input.scale === 'linear' || input.scale === 'log' || input.scale === 'auto' ? input.scale : fallback.scale,
    rangeMode: input.rangeMode === 'manual' || input.rangeMode === 'auto' ? input.rangeMode : fallback.rangeMode,
    min: finiteOrNull(input.min),
    max: finiteOrNull(input.max),
  };
}

function loadCurveViewLayout(projectId: number): Partial<CurveViewLayout> | null {
  const parsed = readStoredJson(`${CURVE_VIEW_LAYOUT_PREFIX}${projectId}`);
  return parsed && typeof parsed === 'object' ? parsed as Partial<CurveViewLayout> : null;
}

function loadLegacyCurveViewSelection(projectId: number): string[] | null {
  return storedStringArray(readStoredJson(`${LEGACY_CURVE_VIEW_STORAGE_PREFIX}${projectId}`));
}

function buildCurveViewLayout(
  availableTracks: SingleCurveTrack[],
  saved: Partial<CurveViewLayout> | null = null,
  legacySelection: string[] | null = null,
): CurveViewLayout {
  const availableKeys = availableTracks.map((track) => curveTrackKey(track.curve));
  const availableKeySet = new Set(availableKeys);
  const savedOrder = storedStringArray(saved?.order) || [];
  const order = [
    ...savedOrder.filter((key, index) => availableKeySet.has(key) && savedOrder.indexOf(key) === index),
    ...availableKeys.filter((key) => !savedOrder.includes(key)),
  ];
  const storedTracks = saved?.tracks && typeof saved.tracks === 'object' ? saved.tracks : {};
  const legacySet = legacySelection === null ? null : new Set(legacySelection);
  const settings = Object.fromEntries(availableTracks.map((track) => {
    const key = curveTrackKey(track.curve);
    const fallback = defaultTrackSettings(track, legacySet ? legacySet.has(key) : true);
    return [key, sanitizeTrackSettings(storedTracks[key], fallback)];
  }));
  return {
    order,
    tracks: settings,
    showDepthValues: typeof saved?.showDepthValues === 'boolean' ? saved.showDepthValues : false,
    libraryOpen: typeof saved?.libraryOpen === 'boolean' ? saved.libraryOpen : true,
  };
}

function saveCurveViewLayout(projectId: number, layout: CurveViewLayout) {
  try {
    localStorage.setItem(`${CURVE_VIEW_LAYOUT_PREFIX}${projectId}`, JSON.stringify(layout));
  } catch {
    // 浏览器禁用本地存储时仍允许本次会话继续编排。
  }
}

// /sweetspot/curves 测井曲线，数据来自 loggingAPI。
export default function SweetCurvesPage() {
  const navigate = useNavigate();
  const { currentProject } = useProjectStore();
  const { curves, loading } = useLogging(currentProject?.id);
  const importedData = useSweetSpotWorkspaceStore((state) => currentProject?.id ? state.importedByProject[currentProject.id] : undefined);
  const importedCurves = useMemo<LogCurve[]>(() => importedData ? buildImportedViewCurves(importedData) : [], [importedData]);
  const sourceCurves = importedData ? importedCurves : curves;
  const pageLoading = importedData ? false : loading;
  const usableCurves = useMemo(() => sourceCurves.filter((curve) =>
    curve.data_points?.some((point) =>
      isFiniteDepthValue(point.depth) && isFiniteDepthValue(point.value))), [sourceCurves]);
  const availableTracks = useMemo(() => buildCurveTracks(usableCurves), [usableCurves]);
  const availableCurveKeys = useMemo(
    () => availableTracks.map((track) => curveTrackKey(track.curve)),
    [availableTracks],
  );
  const availableCurveSignature = availableCurveKeys.join('|');
  const layoutScope = `${currentProject?.id || 0}:${availableCurveSignature}`;
  const fallbackLayout = useMemo(() => buildCurveViewLayout(availableTracks), [availableTracks]);
  const [layout, setLayout] = useState<CurveViewLayout>({
    order: [], tracks: {}, showDepthValues: false, libraryOpen: true,
  });
  const [hydratedLayoutScope, setHydratedLayoutScope] = useState('');
  const [libraryQuery, setLibraryQuery] = useState('');
  const [selectedTrackKey, setSelectedTrackKey] = useState<string | null>(null);
  const [propertiesOpen, setPropertiesOpen] = useState(false);

  useEffect(() => {
    if (!currentProject?.id) {
      setLayout(fallbackLayout);
      setHydratedLayoutScope(layoutScope);
      return;
    }
    const saved = loadCurveViewLayout(currentProject.id);
    const legacySelection = saved ? null : loadLegacyCurveViewSelection(currentProject.id);
    const nextLayout = buildCurveViewLayout(availableTracks, saved, legacySelection);
    setLayout(nextLayout);
    setHydratedLayoutScope(layoutScope);
    setSelectedTrackKey((key) => key && availableCurveKeys.includes(key) ? key : null);
  }, [availableCurveSignature, currentProject?.id, fallbackLayout, layoutScope]);

  const activeLayout = hydratedLayoutScope === layoutScope ? layout : fallbackLayout;
  const availableTrackMap = useMemo(
    () => new Map(availableTracks.map((track) => [curveTrackKey(track.curve), track])),
    [availableTracks],
  );
  const configuredTracks = useMemo<ConfiguredCurveTrack[]>(() => activeLayout.order.flatMap((key) => {
    const track = availableTrackMap.get(key);
    const settings = activeLayout.tracks[key];
    return track && settings ? [{ ...track, key, settings }] : [];
  }), [activeLayout, availableTrackMap]);
  const visibleTracks = useMemo(
    () => configuredTracks.filter((track) => track.settings.visible),
    [configuredTracks],
  );
  const depths = useMemo(() => visibleTracks.flatMap(({ curve }) =>
    curve.data_points.filter((point) => isFiniteDepthValue(point.value)).map((point) => point.depth)),
  [visibleTracks]);
  const depthInteraction = useDepthInteraction(depths, currentProject?.id);
  const chartRef = useRef<HTMLDivElement | null>(null);
  useDepthWheel(chartRef, depthInteraction.zoomAt, depthInteraction.canNavigate, PLOT_TOP, PLOT_BOTTOM);
  const selectedValues = useMemo<DepthValueItem[]>(() => visibleTracks.map(({ curve, settings }) => {
    const point = findNearestByDepth(
      curve.data_points,
      depthInteraction.selectedDepth,
      (item) => item.depth,
    );
    return {
      label: settings.label,
      value: point?.value,
      unit: curve.unit,
      precision: 3,
      color: settings.color,
    };
  }), [depthInteraction.selectedDepth, visibleTracks]);
  const totalPoints = useMemo(
    () => usableCurves.reduce((sum, curve) => sum + (curve.point_count ?? curve.data_points?.length ?? 0), 0),
    [usableCurves],
  );
  const filteredLibraryTracks = useMemo(() => {
    const keyword = libraryQuery.trim().toLowerCase();
    if (!keyword) return configuredTracks;
    return configuredTracks.filter(({ curve, group, base, settings }) => [
      settings.label, curve.mnemonic, curve.description, curve.unit, group.title, displayMetaFor(base, group).title,
    ].some((value) => String(value || '').toLowerCase().includes(keyword)));
  }, [configuredTracks, libraryQuery]);
  const libraryGroups = useMemo(() => {
    const groups = new Map<string, ConfiguredCurveTrack[]>();
    filteredLibraryTracks.forEach((track) => {
      const items = groups.get(track.group.title) || [];
      items.push(track);
      groups.set(track.group.title, items);
    });
    return Array.from(groups, ([title, items]) => ({ title, items }));
  }, [filteredLibraryTracks]);
  const selectedTrack = selectedTrackKey ? configuredTracks.find((track) => track.key === selectedTrackKey) : undefined;
  const selectedOrderKeys = selectedTrack?.settings.visible
    ? activeLayout.order.filter((key) => activeLayout.tracks[key]?.visible)
    : activeLayout.order;

  const commitLayout = (next: CurveViewLayout) => {
    setLayout(next);
    setHydratedLayoutScope(layoutScope);
    if (currentProject?.id) saveCurveViewLayout(currentProject.id, next);
  };

  const patchLayout = (patch: Partial<CurveViewLayout>) => {
    commitLayout({ ...activeLayout, ...patch });
  };

  const patchTrack = (key: string, patch: Partial<CurveTrackSettings>) => {
    const current = activeLayout.tracks[key];
    if (!current) return;
    commitLayout({
      ...activeLayout,
      tracks: { ...activeLayout.tracks, [key]: { ...current, ...patch } },
    });
  };

  const setAllVisible = (visible: boolean) => {
    commitLayout({
      ...activeLayout,
      tracks: Object.fromEntries(Object.entries(activeLayout.tracks).map(([key, settings]) => [
        key, { ...settings, visible },
      ])),
    });
  };

  const moveTrack = (key: string, offset: -1 | 1) => {
    const order = [...activeLayout.order];
    const movingVisibleTrack = Boolean(activeLayout.tracks[key]?.visible);
    const movableKeys = movingVisibleTrack
      ? order.filter((item) => activeLayout.tracks[item]?.visible)
      : order;
    const visibleIndex = movableKeys.indexOf(key);
    const targetKey = movableKeys[visibleIndex + offset];
    const index = order.indexOf(key);
    const target = order.indexOf(targetKey);
    if (index < 0 || target < 0) return;
    [order[index], order[target]] = [order[target], order[index]];
    patchLayout({ order });
  };

  const openTrackProperties = (key: string) => {
    setSelectedTrackKey(key);
    setPropertiesOpen(true);
  };

  const resetLayout = () => {
    const next = buildCurveViewLayout(availableTracks);
    commitLayout(next);
    setSelectedTrackKey(null);
    setPropertiesOpen(false);
  };

  const compactVisibleTracks = () => {
    commitLayout({
      ...activeLayout,
      tracks: Object.fromEntries(Object.entries(activeLayout.tracks).map(([key, settings]) => [
        key, settings.visible ? { ...settings, trackWidth: COMPACT_TRACK_WIDTH } : settings,
      ])),
    });
  };

  if (!currentProject) return <div style={{ padding: 40, color: LTH.textSec }}>请先选择项目</div>;

  return (
    <div className="sweetspot-page-shell sweetspot-curves-page">
      <header className="sweetspot-page-header">
        <div className="curve-view-page-heading">
          <h2>曲线视图</h2>
          <span className="sweetspot-page-subtitle">多道曲线编排与展示</span>
        </div>
        <div className="sweetspot-page-header-actions">
          <Button
            icon={activeLayout.libraryOpen ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => patchLayout({ libraryOpen: !activeLayout.libraryOpen })}
          >
            {activeLayout.libraryOpen ? '收起曲线库' : '曲线库'}
          </Button>
          <Button onClick={() => patchLayout({ showDepthValues: !activeLayout.showDepthValues })}>
            {activeLayout.showDepthValues ? '收起深度值' : '深度值'}
          </Button>
          {depthInteraction.canNavigate && (
            <Button onClick={depthInteraction.resetView}>全部深度</Button>
          )}
          <Button onClick={() => navigate('/datahub/import')}>导入数据</Button>
          <Button onClick={() => navigate('/sweetspot/results')}>返回评价工作台</Button>
        </div>
      </header>
      <main className="sweetspot-page-content sweetspot-curves-content">
        {pageLoading ? (
          <div className="sweetspot-curves-feedback">
            <Spin tip="正在加载测井曲线…" />
          </div>
        ) : availableTracks.length === 0 ? (
          <div className="sweetspot-curves-feedback">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无可显示的数值曲线，请先导入并应用数据"
            >
              <Button type="primary" onClick={() => navigate('/datahub/import')}>前往数据导入</Button>
            </Empty>
          </div>
        ) : (
          <div className={`curve-view-studio${activeLayout.libraryOpen ? ' has-library' : ''}${propertiesOpen && selectedTrack ? ' has-properties' : ''}`}>
            {activeLayout.libraryOpen && (
              <aside className="curve-view-library" aria-label="曲线库">
                <div className="curve-view-panel-head">
                  <div>
                    <strong>曲线库</strong>
                    <span>{visibleTracks.length}/{availableTracks.length} 道显示</span>
                  </div>
                  <Button type="text" size="small" aria-label="收起曲线库" icon={<CloseOutlined />} onClick={() => patchLayout({ libraryOpen: false })} />
                </div>
                <div className="curve-view-library-tools">
                  <Input.Search
                    allowClear
                    size="small"
                    value={libraryQuery}
                    placeholder="搜索名称、代号或单位"
                    onChange={(event) => setLibraryQuery(event.target.value)}
                  />
                  <Space size={4}>
                    <Button size="small" onClick={() => setAllVisible(true)}>全部显示</Button>
                    <Button size="small" onClick={() => setAllVisible(false)}>全部隐藏</Button>
                  </Space>
                </div>
                <div className="curve-view-library-list">
                  {libraryGroups.length ? libraryGroups.map((group) => (
                    <section key={group.title} className="curve-view-library-group">
                      <div className="curve-view-library-group-title">
                        <span>{group.title}</span>
                        <span>{group.items.filter((track) => track.settings.visible).length}/{group.items.length}</span>
                      </div>
                      {group.items.map((track) => {
                        const count = track.curve.point_count ?? track.curve.data_points.length;
                        const orderIndex = activeLayout.order.indexOf(track.key) + 1;
                        return (
                          <div key={track.key} className={`curve-view-library-item${selectedTrackKey === track.key ? ' is-selected' : ''}`}>
                            <Checkbox
                              aria-label={`显示 ${track.curve.mnemonic}`}
                              checked={track.settings.visible}
                              onChange={(event) => patchTrack(track.key, { visible: event.target.checked })}
                            />
                            <button type="button" onClick={() => openTrackProperties(track.key)}>
                              <span className="curve-view-color-mark" style={{ background: track.settings.color }} />
                              <span className="curve-view-library-copy">
                                <strong>{track.settings.label}</strong>
                                <small>{String(orderIndex).padStart(2, '0')} · {track.curve.mnemonic}{track.curve.unit ? ` / ${track.curve.unit}` : ''} · {count.toLocaleString()} 点</small>
                              </span>
                              <SettingOutlined />
                            </button>
                          </div>
                        );
                      })}
                    </section>
                  )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未找到匹配曲线" />}
                </div>
              </aside>
            )}

            <section className="curve-view-canvas-column">
              <div className="curve-view-canvas-toolbar">
                <div className="curve-view-canvas-status">
                  <strong>曲线画布</strong>
                  <Tag>{visibleTracks.length} 道</Tag>
                  <span>{totalPoints.toLocaleString()} 个数据点</span>
                </div>
                <Space size={4}>
                  <Tooltip title="将当前显示道统一压缩为 104 px">
                    <Button size="small" icon={<CompressOutlined />} onClick={compactVisibleTracks}>紧凑排列</Button>
                  </Tooltip>
                  {selectedTrack && !propertiesOpen && (
                    <Button size="small" icon={<SettingOutlined />} onClick={() => setPropertiesOpen(true)}>道属性</Button>
                  )}
                  <Button size="small" icon={<ReloadOutlined />} onClick={resetLayout}>恢复默认</Button>
                </Space>
              </div>
              <div className="curve-view-plot-stack">
                {visibleTracks.length === 0 ? (
                  <div className="sweetspot-curves-feedback">
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前未显示曲线">
                      <Button type="primary" onClick={() => setAllVisible(true)}>显示全部曲线</Button>
                    </Empty>
                  </div>
                ) : (
                  <div
                    className="sweetspot-curves-chart"
                    ref={chartRef}
                    onMouseDown={(event) => {
                      if (event.button !== 0) return;
                      const rect = event.currentTarget.getBoundingClientRect();
                      depthInteraction.startPan(event.clientY, Math.max(1, rect.height - PLOT_TOP - PLOT_BOTTOM));
                    }}
                    title="滚轮缩放 · 拖动平移 · 拖动横向深度游标"
                    style={{ cursor: depthInteraction.isDraggingCursor
                      ? 'ns-resize'
                      : depthInteraction.isPanningChart ? 'grabbing' : 'grab' }}
                  >
                    {depthInteraction.visibleRange && (
                      <>
                        <CurvesView tracks={visibleTracks} viewRange={depthInteraction.visibleRange} />
                        <DepthCursorOverlay
                          selectedDepth={depthInteraction.selectedDepth}
                          visibleRange={depthInteraction.visibleRange}
                          plotTop={PLOT_TOP}
                          plotBottom={PLOT_BOTTOM}
                          onDragStart={(clientY) => {
                            const rect = chartRef.current?.getBoundingClientRect();
                            if (!rect) return;
                            depthInteraction.startCursorDrag(clientY, {
                              top: rect.top, height: rect.height, plotTop: PLOT_TOP, plotBottom: PLOT_BOTTOM,
                            });
                          }}
                        />
                      </>
                    )}
                  </div>
                )}
                {activeLayout.showDepthValues && visibleTracks.length > 0 && (
                  <div className="curve-view-depth-dock">
                    <DepthValuePanel
                      depth={depthInteraction.selectedDepth}
                      items={selectedValues}
                      layout="horizontal"
                      height={112}
                      title="深度值"
                    />
                  </div>
                )}
              </div>
            </section>

            {propertiesOpen && selectedTrack && (
              <TrackPropertiesPanel
                track={selectedTrack}
                index={selectedOrderKeys.indexOf(selectedTrack.key)}
                total={selectedOrderKeys.length}
                onClose={() => setPropertiesOpen(false)}
                onChange={(patch) => patchTrack(selectedTrack.key, patch)}
                onMove={(offset) => moveTrack(selectedTrack.key, offset)}
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function TrackPropertiesPanel({
  track,
  index,
  total,
  onClose,
  onChange,
  onMove,
}: {
  track: ConfiguredCurveTrack;
  index: number;
  total: number;
  onClose: () => void;
  onChange: (patch: Partial<CurveTrackSettings>) => void;
  onMove: (offset: -1 | 1) => void;
}) {
  const { curve, settings } = track;
  const manualRangeInvalid = settings.rangeMode === 'manual'
    && (settings.min === null || settings.max === null || settings.min >= settings.max);
  return (
    <aside className="curve-view-properties" aria-label="道属性">
      <div className="curve-view-panel-head">
        <div>
          <strong>道属性</strong>
          <span>{curve.mnemonic}{curve.unit ? ` · ${curve.unit}` : ''}</span>
        </div>
        <Button type="text" size="small" aria-label="收起道属性" icon={<CloseOutlined />} onClick={onClose} />
      </div>
      <div className="curve-view-properties-scroll">
        <section className="curve-view-property-section">
          <label htmlFor={`curve-label-${track.key}`}>道名称</label>
          <Input
            id={`curve-label-${track.key}`}
            value={settings.label}
            maxLength={40}
            onChange={(event) => onChange({ label: event.target.value })}
          />
          <div className="curve-view-source-meta">
            <span>数据列</span><strong>{curve.mnemonic}</strong>
            <span>有效点</span><strong>{(curve.point_count ?? curve.data_points.length).toLocaleString()}</strong>
          </div>
        </section>

        <section className="curve-view-property-section">
          <label>曲线颜色</label>
          <div className="curve-view-color-editor">
            <input
              type="color"
              aria-label="曲线颜色"
              value={settings.color}
              onChange={(event) => onChange({ color: event.target.value.toUpperCase() })}
            />
            <Input value={settings.color.toUpperCase()} readOnly />
          </div>
          <div className="curve-view-color-presets" aria-label="预设颜色">
            {CURVE_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                aria-label={`使用颜色 ${color}`}
                className={settings.color.toUpperCase() === color ? 'is-active' : ''}
                style={{ background: color }}
                onClick={() => onChange({ color })}
              />
            ))}
          </div>
          <div className="curve-view-property-grid">
            <label htmlFor={`curve-line-width-${track.key}`}>线宽</label>
            <InputNumber
              id={`curve-line-width-${track.key}`}
              min={0.6}
              max={4}
              step={0.2}
              precision={1}
              value={settings.lineWidth}
              addonAfter="px"
              onChange={(value) => onChange({ lineWidth: typeof value === 'number' ? value : 1.4 })}
            />
            <label htmlFor={`curve-track-width-${track.key}`}>道宽</label>
            <InputNumber
              id={`curve-track-width-${track.key}`}
              min={96}
              max={360}
              step={8}
              value={settings.trackWidth}
              addonAfter="px"
              onChange={(value) => onChange({ trackWidth: typeof value === 'number' ? value : DEFAULT_TRACK_WIDTH })}
            />
          </div>
        </section>

        <section className="curve-view-property-section">
          <label>刻度类型</label>
          <Segmented<CurveScaleMode>
            block
            value={settings.scale}
            options={[
              { label: '自动', value: 'auto' },
              { label: '线性', value: 'linear' },
              { label: '对数', value: 'log' },
            ]}
            onChange={(value) => onChange({ scale: value })}
          />
          <label>取值范围</label>
          <Segmented<CurveRangeMode>
            block
            value={settings.rangeMode}
            options={[
              { label: '自动', value: 'auto' },
              { label: '指定范围', value: 'manual' },
            ]}
            onChange={(value) => onChange({ rangeMode: value })}
          />
          {settings.rangeMode === 'manual' && (
            <>
              <div className="curve-view-range-inputs">
                <InputNumber
                  aria-label="范围下限"
                  placeholder="下限"
                  value={settings.min}
                  onChange={(value) => onChange({ min: finiteOrNull(value) })}
                />
                <span>—</span>
                <InputNumber
                  aria-label="范围上限"
                  placeholder="上限"
                  value={settings.max}
                  onChange={(value) => onChange({ max: finiteOrNull(value) })}
                />
              </div>
              {manualRangeInvalid && <span className="curve-view-field-error">上限必须大于下限</span>}
            </>
          )}
        </section>

        <section className="curve-view-property-section">
          <label>显示顺序</label>
          <div className="curve-view-order-control">
            <Button icon={<ArrowLeftOutlined />} disabled={index <= 0} onClick={() => onMove(-1)}>左移</Button>
            <span>{index + 1} / {total}</span>
            <Button icon={<ArrowRightOutlined />} disabled={index >= total - 1} onClick={() => onMove(1)}>右移</Button>
          </div>
          <Button
            block
            icon={settings.visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => onChange({ visible: !settings.visible })}
          >
            {settings.visible ? '隐藏此道' : '显示此道'}
          </Button>
        </section>
      </div>
    </aside>
  );
}

function CurvesView({ tracks, viewRange }: { tracks: ConfiguredCurveTrack[]; viewRange: [number, number] }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const updateWidth = () => setViewportWidth(container.clientWidth);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    window.addEventListener('resize', updateWidth);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const canvasWidth = Math.max(
    viewportWidth,
    LEFT_AXIS_WIDTH + 16
      + tracks.reduce((sum, track) => sum + track.settings.trackWidth, 0)
      + Math.max(0, tracks.length - 1) * TRACK_GAP,
  );

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = cv.getBoundingClientRect();
    const W = canvasWidth;
    const H = rect.height;
    if (W <= 0 || H <= 0) return;

    cv.width = Math.max(1, Math.floor(W * dpr));
    cv.height = Math.max(1, Math.floor(H * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = LTH.canvasBg;
    ctx.fillRect(0, 0, W, H);

    if (!tracks.length) return;

    const [dMin, dMax] = viewRange;
    if (!Number.isFinite(dMin) || !Number.isFinite(dMax)) return;

    const padT = PLOT_TOP;
    const padB = PLOT_BOTTOM;
    const plotH = Math.max(40, H - padT - padB);
    const yAt = (depth: number) => padT + ((depth - dMin) / (dMax - dMin || 1)) * plotH;

    ctx.fillStyle = LTH.textMuted;
    ctx.font = `10px ${LTH.fontMono}`;
    ctx.textAlign = 'right';
    const span = dMax - dMin;
    const dStep = span > 1200 ? 200 : span > 500 ? 100 : span > 180 ? 50 : span > 70 ? 20 : 10;
    for (let depth = Math.ceil(dMin / dStep) * dStep; depth <= dMax; depth += dStep) {
      const y = yAt(depth);
      ctx.strokeStyle = LTH.grid;
      ctx.beginPath();
      ctx.moveTo(LEFT_AXIS_WIDTH, y);
      ctx.lineTo(W - 8, y);
      ctx.stroke();
      ctx.fillStyle = LTH.textMuted;
      ctx.fillText(formatValue(depth), LEFT_AXIS_WIDTH - 5, y + 3);
    }

    ctx.save();
    ctx.translate(13, padT + plotH / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.fillStyle = LTH.textSec;
    ctx.font = `11px ${LTH.fontUI}`;
    ctx.fillText('深度 (m)', 0, 0);
    ctx.restore();

    let trackOffset = LEFT_AXIS_WIDTH;
    tracks.forEach(({ curve, group, base, settings }) => {
      const display = displayMetaFor(base, group);
      const trackWidth = settings.trackWidth;
      const x0 = trackOffset;
      const x1 = x0 + trackWidth;
      trackOffset = x1 + TRACK_GAP;

      ctx.strokeStyle = LTH.grid;
      ctx.lineWidth = 1;
      ctx.strokeRect(x0, padT, trackWidth, plotH);

      ctx.fillStyle = LTH.panelHeaderBg;
      ctx.fillRect(x0, 8, trackWidth, 24);
      ctx.fillStyle = settings.color;
      ctx.fillRect(x0, 8, 3, 52);
      ctx.fillStyle = LTH.textSec;
      ctx.font = `10px ${LTH.fontUI}`;
      drawTextFit(ctx, settings.label, x0 + trackWidth / 2, 24, trackWidth - 12, 'center');

      ctx.fillStyle = LTH.text;
      ctx.font = `bold 11px ${LTH.fontUI}`;
      drawTextFit(ctx, `${curve.mnemonic}${curve.unit ? ` (${curve.unit})` : ''}`, x0 + trackWidth / 2, 46, trackWidth - 12, 'center');

      const count = curve.point_count ?? curve.data_points.length;
      ctx.fillStyle = LTH.textMuted;
      ctx.font = `9px ${LTH.fontUI}`;
      drawTextFit(ctx, `${count.toLocaleString()} 点`, x0 + trackWidth / 2, 60, trackWidth - 12, 'center');

      const automaticLogScale = Boolean(display.logScale ?? group.logScale);
      const useLogScale = settings.scale === 'log' || (settings.scale === 'auto' && automaticLogScale);

      let localMin = Infinity;
      let localMax = -Infinity;
      for (const point of curve.data_points) {
        if (!finiteNumber(point.value)) continue;
        if (useLogScale && point.value <= 0) continue;
        if (point.value < localMin) localMin = point.value;
        if (point.value > localMax) localMax = point.value;
      }

      if (!Number.isFinite(localMin) || !Number.isFinite(localMax)) {
        ctx.fillStyle = LTH.textMuted;
        ctx.font = `10px ${LTH.fontUI}`;
        drawTextFit(ctx, '无有效值', x0 + trackWidth / 2, padT + plotH / 2, trackWidth - 12, 'center');
        return;
      }

      const storedMin = finiteNumber(curve.min_value) ? curve.min_value : localMin;
      const storedMax = finiteNumber(curve.max_value) ? curve.max_value : localMax;
      let configuredMin = storedMin;
      let configuredMax = storedMax;
      if (
        settings.rangeMode === 'manual'
        && finiteNumber(settings.min)
        && finiteNumber(settings.max)
        && settings.min < settings.max
        && (!useLogScale || settings.min > 0)
      ) {
        configuredMin = settings.min;
        configuredMax = settings.max;
      }
      const vMin = useLogScale && configuredMin <= 0 ? localMin : configuredMin;
      const vMax = useLogScale && configuredMax <= 0 ? localMax : configuredMax;

      if (!Number.isFinite(vMin) || !Number.isFinite(vMax)) return;

      const logMin = useLogScale ? Math.log10(Math.max(vMin, Number.MIN_VALUE)) : 0;
      const logMax = useLogScale ? Math.log10(Math.max(vMax, Number.MIN_VALUE)) : 0;
      const xAt = (value: number) => {
        if (useLogScale) {
          if (value <= 0) return null;
          return x0 + ((Math.log10(value) - logMin) / (logMax - logMin || 1)) * trackWidth;
        }
        return x0 + ((value - vMin) / (vMax - vMin || 1)) * trackWidth;
      };

      for (let grid = 1; grid < 4; grid += 1) {
        const x = x0 + (trackWidth * grid) / 4;
        ctx.strokeStyle = LTH.grid;
        ctx.beginPath();
        ctx.moveTo(x, padT);
        ctx.lineTo(x, padT + plotH);
        ctx.stroke();
      }

      const color = settings.color;
      ctx.fillStyle = color;
      ctx.font = `8px ${LTH.fontMono}`;
      ctx.textAlign = 'left';
      ctx.fillText(formatValue(vMin), x0 + 3, padT - 6);
      ctx.textAlign = 'right';
      ctx.fillText(formatValue(vMax), x1 - 3, padT - 6);

      ctx.strokeStyle = color;
      ctx.lineWidth = settings.lineWidth;
      ctx.beginPath();
      let started = false;
      for (const point of curve.data_points) {
        if (!finiteNumber(point.depth) || !finiteNumber(point.value)) continue;
        const rawX = xAt(point.value);
        if (rawX === null || !Number.isFinite(rawX)) continue;
        const x = Math.max(x0, Math.min(x1, rawX));
        const y = yAt(point.depth);
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    });
  }, [canvasWidth, tracks, viewRange]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
      <canvas ref={canvasRef} style={{ width: canvasWidth, height: '100%', display: 'block' }} />
    </div>
  );
}
